const KEY = 'intimsync_web_outbox_v3';
const MAX_BACKOFF_MS = 1000 * 60 * 30;

export type WebOutboxItem = {
  id: string;
  kind: string;
  payload: any;
  createdAt: number;
  updatedAt: number;
  retryCount: number;
  nextAttemptAt: number;
  lastError?: string;
};

function readAll(): WebOutboxItem[] {
  try {
    const raw = window.localStorage.getItem(KEY);
    if (!raw) return [];
    const items = JSON.parse(raw);
    return Array.isArray(items) ? items : [];
  } catch {
    return [];
  }
}

function writeAll(items: WebOutboxItem[]) {
  window.localStorage.setItem(KEY, JSON.stringify(items));
}

function computeBackoffMs(retryCount: number) {
  const base = Math.max(1, retryCount || 0);
  return Math.min(MAX_BACKOFF_MS, 1500 * Math.pow(2, base - 1));
}

export async function listOutbox(now = Date.now()): Promise<WebOutboxItem[]> {
  return readAll()
    .filter((x) => (x.nextAttemptAt || 0) <= now)
    .sort((a, b) => a.createdAt - b.createdAt);
}

export async function listAllOutbox(): Promise<WebOutboxItem[]> {
  return readAll().sort((a, b) => a.createdAt - b.createdAt);
}

export async function enqueueOutbox(kind: string, payload: any): Promise<WebOutboxItem> {
  const now = Date.now();
  const item: WebOutboxItem = {
    id: `web_outbox_${now}_${Math.random().toString(36).slice(2, 10)}`,
    kind,
    payload,
    createdAt: now,
    updatedAt: now,
    retryCount: 0,
    nextAttemptAt: now,
  };
  const items = readAll();
  items.push(item);
  writeAll(items);
  return item;
}

export async function removeOutbox(id: string): Promise<void> {
  writeAll(readAll().filter((x) => x.id !== id));
}

export async function markOutboxFailure(id: string, error?: string): Promise<void> {
  const items = readAll().map((x) => {
    if (x.id !== id) return x;
    const retryCount = (x.retryCount || 0) + 1;
    const now = Date.now();
    return {
      ...x,
      updatedAt: now,
      retryCount,
      nextAttemptAt: now + computeBackoffMs(retryCount),
      lastError: error ? String(error).slice(0, 400) : x.lastError,
    };
  });
  writeAll(items);
}
